import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Stamp, FileCheck2, Settings, Plus, Trash2, ShieldCheck, Clock, AlertTriangle, CheckCircle2, Upload, FileText } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Spinner, StatCard } from "@/components/portal/ui";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const DOC_STATUSES = ["Pending", "Submitted", "Cleared", "Rejected"];
const STATUS_STYLE = {
  Cleared: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  "In Progress": "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
  Submitted: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
  Pending: "bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300",
  Held: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
  Rejected: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
};

function CorridorConfig({ corridor, canEdit, onSaved }) {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState(corridor.documents.map((d) => ({ ...d })));
  const save = async () => {
    try {
      await api.put(`/customs/corridors/${corridor.id}/documents`, { documents: docs });
      toast.success("Document requirements updated");
      setOpen(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid={`corridor-${corridor.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent text-primary flex items-center justify-center"><Stamp size={20} /></div>
          <div>
            <h3 className="font-heading font-bold text-sm">{corridor.name}</h3>
            <p className="text-xs text-muted-foreground">{corridor.route}</p>
          </div>
        </div>
        <span className="text-xs text-muted-foreground">{corridor.active_clearances} active</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {corridor.documents.map((d) => (
          <span key={d.key} className="text-xs px-2 py-0.5 rounded-md bg-muted text-muted-foreground">{d.name}{d.required ? " *" : ""}</span>
        ))}
      </div>
      {canEdit && (
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setDocs(corridor.documents.map((d) => ({ ...d }))); }}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="mt-4" data-testid={`corridor-config-${corridor.id}`}><Settings size={14} className="mr-1.5" /> Configure documents</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{corridor.name} — required documents</DialogTitle><DialogDescription>Configure the customs paperwork required to clear this corridor.</DialogDescription></DialogHeader>
            <div className="space-y-2">
              {docs.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={d.name} onChange={(e) => setDocs(docs.map((x, j) => j === i ? { ...x, name: e.target.value, key: e.target.value.toLowerCase().replace(/\s+/g, "_") } : x))} data-testid={`doc-name-${i}`} />
                  <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                    <input type="checkbox" checked={d.required} onChange={(e) => setDocs(docs.map((x, j) => j === i ? { ...x, required: e.target.checked } : x))} /> req
                  </label>
                  <Button variant="ghost" size="icon" onClick={() => setDocs(docs.filter((_, j) => j !== i))} data-testid={`doc-del-${i}`}><Trash2 size={14} /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setDocs([...docs, { key: "", name: "", required: true }])} data-testid="doc-add"><Plus size={14} className="mr-1.5" /> Add document</Button>
            </div>
            <DialogFooter><Button className="bg-primary hover:bg-red-700" onClick={save} data-testid={`corridor-save-${corridor.id}`}>Save requirements</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function DocRow({ clearance, doc, onStatus, onUploaded }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem("ebik_token");
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/customs/clearances/${clearance.shipment_id}/documents/${doc.key}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(r.data);
      toast.success(`${doc.name} attached`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  return (
    <div className="rounded-lg border border-border p-2.5" data-testid={`clr-doc-${clearance.tracking_number}-${doc.key}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm">{doc.name}{doc.required && <span className="text-primary">*</span>}</span>
        <select value={doc.status} onChange={(e) => onStatus(clearance.shipment_id, doc.key, e.target.value)}
          data-testid={`clr-doc-select-${clearance.tracking_number}-${doc.key}`}
          className={`text-xs font-semibold rounded-md px-2 py-1 border border-border ${STATUS_STYLE[doc.status] || ""}`}>
          {DOC_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden" onChange={upload} data-testid={`clr-doc-file-${clearance.tracking_number}-${doc.key}`} />
        <button onClick={() => fileRef.current?.click()} disabled={busy}
          className="text-xs font-semibold text-muted-foreground hover:text-primary flex items-center gap-1 disabled:opacity-50"
          data-testid={`clr-doc-upload-${clearance.tracking_number}-${doc.key}`}>
          <Upload size={12} /> {busy ? "Uploading…" : (doc.file ? "Replace" : "Attach")}
        </button>
        {doc.file && (
          <a href={`${BACKEND}/api/files/${doc.file.path}?auth=${token}`} target="_blank" rel="noreferrer"
            className="text-xs font-semibold text-primary flex items-center gap-1"
            data-testid={`clr-doc-view-${clearance.tracking_number}-${doc.key}`}>
            <FileText size={12} /> View
          </a>
        )}
      </div>
    </div>
  );
}

export default function Customs() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "managing_director"].includes(user?.role);
  const [corridors, setCorridors] = useState([]);
  const [clearances, setClearances] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, cl] = await Promise.all([api.get("/customs/corridors"), api.get("/customs/clearances")]);
      setCorridors(c.data.corridors); setClearances(cl.data.clearances); setSummary(cl.data.summary);
    } catch { toast.error("Failed to load customs data"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const setDoc = async (sid, key, status) => {
    try {
      const r = await api.patch(`/customs/clearances/${sid}/documents/${key}`, { status });
      setClearances((prev) => prev.map((c) => c.shipment_id === sid ? r.data : c));
      toast.success(`Document ${status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <PageHeader title="Cross-Border Customs" subtitle="Configurable clearance workflows · Beitbridge & Lebombo corridors" />

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard testid="cust-total" label="Cross-border loads" value={summary.total} icon={Stamp} />
          <StatCard testid="cust-cleared" label="Cleared" value={summary.cleared} icon={CheckCircle2} accent="text-emerald-600" />
          <StatCard testid="cust-progress" label="In progress" value={summary.in_progress} icon={Clock} accent="text-blue-600" />
          <StatCard testid="cust-pending" label="Pending" value={summary.pending} icon={FileCheck2} accent="text-slate-600" />
          <StatCard testid="cust-held" label="Held" value={summary.held} icon={AlertTriangle} accent="text-red-600" />
        </div>
      )}

      <div className="rounded-xl border border-blue-200 bg-blue-50 dark:bg-blue-500/10 p-4 mb-6 text-sm text-blue-800 dark:text-blue-300 flex items-start gap-2">
        <ShieldCheck size={18} className="mt-0.5 shrink-0" />
        <span>Clearances are auto-created for shipments routed through a configured border corridor. Track each document from Pending → Submitted → Cleared; a load is Cleared once all required documents are signed off.</span>
      </div>

      <h2 className="eyebrow text-muted-foreground mb-3">Border Corridors</h2>
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {corridors.map((c) => <CorridorConfig key={c.id} corridor={c} canEdit={canEdit} onSaved={load} />)}
      </div>

      <h2 className="eyebrow text-muted-foreground mb-3">Active Clearances</h2>
      <div className="space-y-4">
        {clearances.length === 0 && <p className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">No cross-border shipments currently require clearance.</p>}
        {clearances.map((c) => (
          <div key={c.shipment_id} className="rounded-xl border border-border bg-card p-5" data-testid={`clearance-${c.tracking_number}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <span className="font-mono font-bold text-sm">{c.tracking_number}</span>
                <span className="text-sm text-muted-foreground">{c.route}</span>
                <span className="text-xs px-2 py-0.5 rounded-md bg-accent text-primary font-semibold">{c.corridor_name}</span>
              </div>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${STATUS_STYLE[c.status] || STATUS_STYLE.Pending}`}>{c.status}</span>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-2">
              {c.documents.map((d) => (
                <DocRow key={d.key} clearance={c} doc={d} onStatus={setDoc}
                  onUploaded={(updated) => setClearances((prev) => prev.map((x) => x.shipment_id === c.shipment_id ? updated : x))} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
